import { useState } from 'react';
import { Box, Button, Chip, Tooltip, TintedStatusPill, Typography } from '../../ui/core';
import { SHADOW_LABEL, formatJson, shadowTooltip } from './sdpFormatters';

// An enum value as a coloured pill. The label is the enum verbatim.
export const EnumPill = ({ value, palettes }) =>
  value ? <TintedStatusPill palette={palettes[value] ?? null} label={value} /> : <Typography component="span">—</Typography>;

// Marks a value SDP computed but never paid (log_only/off).
export const ShadowChip = ({ mode }) => (
  <Tooltip title={shadowTooltip(mode)}>
    <Chip size="small" variant="outlined" label={SHADOW_LABEL} data-testid="shadow-chip" sx={{ fontStyle: 'italic', ml: '6px' }} />
  </Tooltip>
);

// Collapsible raw-JSON block (evidence, raw flag values).
export const JsonBlock = ({ value, label = 'raw JSON', defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Box>
      <Button size="small" variant="text" onClick={() => setOpen((o) => !o)} sx={{ px: 0, minWidth: 0 }}>
        {open ? `Hide ${label}` : `Show ${label}`}
      </Button>
      {open && <Pre>{formatJson(value)}</Pre>}
    </Box>
  );
};

export const Pre = ({ children }) => (
  <Box
    component="pre"
    sx={{
      m: 0,
      p: '8px 10px',
      fontSize: '12px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      borderRadius: '6px',
      backgroundColor: (theme) => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#F2F2F3'),
    }}
  >
    {children}
  </Box>
);

// A guard event in plain English, from the API's `description` (headline,
// detail, response, check). `compact` shows the headline only. Renders nothing
// for an API that predates it; the enum fields stay beside it either way.
export const GuardDescription = ({ event, compact = false }) => {
  const d = event && event.description;
  if (!d) return null;
  if (compact) return <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{d.headline}</Typography>;
  return (
    <Box data-testid={`description-${event.id}`} sx={{ maxWidth: 480 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{d.headline}</Typography>
      <Typography sx={{ fontSize: '13px' }}>{d.detail}</Typography>
      <Typography sx={{ fontSize: '13px' }}>{d.response}</Typography>
      {d.check && (
        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>{`What to check: ${d.check}`}</Typography>
      )}
    </Box>
  );
};

export const SectionTitle = ({ children }) => (
  <Typography sx={{ fontSize: '13px', fontWeight: 600, mt: '12px', mb: '6px' }}>{children}</Typography>
);

export const Muted = ({ children }) => (
  <Typography color="text.secondary" sx={{ fontSize: '13px' }}>
    {children}
  </Typography>
);
